import type { IProfilerOptions } from '../types/profiler.js';
import { ABaseMark } from './Mark.js';
import { InvalidProfilerAdapter } from '../errors/InvalidAdapter.js';

/**
 * BaseProfiler
 *
 * Profiler singleton class
 */
export class BaseProfiler<InstanceType, MarkerType extends ABaseMark<unknown>> {
  protected enabled = false;

  protected adapter = null;

  protected static instance: BaseProfiler<unknown, ABaseMark<unknown>> | null = null;

  protected marks: MarkerType[] = [];

  /**
   * constructor
   *
   * @param {InstanceType} adapter
   * @param {IProfilerOptions} options
   * @returns class instance
   */
  protected constructor(adapter: InstanceType, options?: IProfilerOptions) {
    this.enabled = (options && options.enabled) || false;
    this.adapter = adapter;
    this.marks = [];

    return this;
  }

  /**
   * getInstance
   *
   * @param {InstanceType} adapter
   * @param {IProfilerOptions} options
   * @returns {Profiler<InstanceType, ABaseMark<unknown>>}
   */
  public static getInstance<InstanceType>(
    adapter?: InstanceType,
    options?: IProfilerOptions,
  ): BaseProfiler<InstanceType, ABaseMark<unknown>> {
    // Check if class has been instantiated
    if (!this.instance) {
      if (!adapter) {
        throw new InvalidProfilerAdapter('Missing or invalid adapter instance provided for profiler.');
      }

      this.instance = new this<InstanceType, ABaseMark<unknown>>(adapter, options);
    }

    return this.instance as BaseProfiler<InstanceType, ABaseMark<unknown>>;
  }

  /**
   * findMarkIndex
   *
   * @param {string} name
   * @returns {number}
   */
  protected findMarkIndex(name: string): number {
    return this.marks.findIndex((mark) => mark.name === name);
  }

  /**
   * findMark
   *
   * @param {string} name
   * @returns {MarkerType | undefined}
   */
  protected findMark(name: string): MarkerType | undefined {
    const markIndex = this.findMarkIndex(name);

    return markIndex > -1 ? this.marks[markIndex] : undefined;
  }

  /**
   * storeMark
   *
   * @param {MarkerType} mark
   * @returns {MarkerType[]}
   */
  public storeMark(mark: MarkerType): MarkerType[] {
    this.marks.push(mark);

    return this.marks;
  }

  /**
   * clear
   *
   * @returns {void}
   */
  public clear(): void {
    if (!this.enabled) {
      return;
    }

    this.marks.forEach((mark) => {
      if (mark) {
        mark.end();
      }
    });

    this.marks = [];
  }

  /**
   * error
   *
   * @param {string} error
   * @returns {void}
   */
  public error(error: string): void {
    console.error(error);
  }
}
